import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';

const StoreSetup = ({ onConnected }) => {
  const navigate = useNavigate();
  const [platform, setPlatform] = useState('woocommerce');
  const [storeUrl, setStoreUrl] = useState('');
  const [consumerKey, setConsumerKey] = useState('');
  const [consumerSecret, setConsumerSecret] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState(null);
  const [subscription, setSubscription] = useState({ plan: null, status: null });
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const [checkingConnection, setCheckingConnection] = useState(true);
  const [useManualShopify, setUseManualShopify] = useState(false);
  const [shopifyClientId, setShopifyClientId] = useState('');
  const [shopifyClientSecret, setShopifyClientSecret] = useState('');
  const [copiedField, setCopiedField] = useState(null); // Track which field was copied

  useEffect(() => {
    // Check if store is already connected first - redirect immediately if connected
    const checkStoreConnection = async () => {
      try {
        const connectionRes = await api.get('/api/stores/check-connection');
        const hasConnection = connectionRes.data.hasConnection;
        
        if (hasConnection) {
          // Store is already connected, redirect to dashboard immediately
          navigate('/dashboard', { replace: true });
          return;
        }
      } catch (err) {
        // If check fails, continue to show setup page
        console.error('Error checking store connection:', err);
      } finally {
        setCheckingConnection(false);
      }
    };

    checkStoreConnection();
  }, [navigate]);

  useEffect(() => {
    // Check if we have session_id from Stripe checkout redirect
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    
    const fetchSubscription = async () => {
      try {
        // If we have session_id, check the session status first
        if (sessionId) {
          try {
            console.log('StoreSetup: Processing Stripe session:', sessionId);
            const sessionResponse = await api.get(`/api/subscribe/check-session?session_id=${sessionId}`);
            console.log('StoreSetup: Session response:', sessionResponse.data);
            
            if (sessionResponse.data.success) {
              // Subscription was updated, now fetch the latest status
              const statusResponse = await api.get('/api/subscribe/status');
              setSubscription(statusResponse.data);
              setSubscriptionLoading(false);
              return;
            }
          } catch (sessionErr) {
            console.error('StoreSetup: Error checking session:', sessionErr);
            // Continue with normal subscription check
          }
        }
        
        // Normal subscription status check
        const response = await api.get('/api/subscribe/status');
        setSubscription(response.data);
      } catch (err) {
        console.error('Error fetching subscription:', err);
        setSubscription({ plan: null, status: null });
      } finally {
        setSubscriptionLoading(false);
      }
    };
    fetchSubscription();
  }, []);

  // Show loading state while checking connection or fetching subscription
  if (checkingConnection || subscriptionLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Show subscription required message if not subscribed (but allow free plan)
  if (subscription.status !== 'active' && subscription.status !== 'trialing' && subscription.plan !== 'free') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-6 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Subscription Required</h2>
          <p className="text-gray-600 mb-6">
            You need an active subscription to connect your store and access CartResQ features.
          </p>
          <a
            href="/pricing"
            className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Choose a Plan
          </a>
        </div>
      </div>
    );
  }

  // Copy to clipboard helper with temporary feedback
  const copyToClipboard = async (text, fieldName) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Normalize store domain for deep links
  const sanitizeShopDomain = (url) => {
    if (!url) return '';
    let domain = url.trim().toLowerCase();
    domain = domain.replace(/^https?:\/\//, '');
    domain = domain.replace(/\/$/, '');
    domain = domain.replace(/\.myshopify\.com$/, '');
    return domain;
  };

  const handleShopifyOAuth = async () => {
    if (!storeUrl.trim()) {
      setError('Please enter your Shopify store domain (e.g., my-store.myshopify.com)');
      return;
    }

    if (!shopifyClientId.trim() || !shopifyClientSecret.trim()) {
      setError('Please enter your Shopify app Client ID and Client Secret');
      return;
    }

    setIsConnecting(true);
    setError(null);

    try {
      // Clean up the store URL for OAuth
      let shopDomain = storeUrl.trim().toLowerCase();
      shopDomain = shopDomain.replace(/^https?:\/\//, '');
      shopDomain = shopDomain.replace(/\/$/, '');
      shopDomain = shopDomain.replace(/\.myshopify\.com$/, '');

      if (!shopDomain) {
        throw new Error('Please enter a valid Shopify store domain');
      }

      // Make authenticated API call to get OAuth URL
      const response = await api.get('/api/stores/shopify/oauth', {
        params: { 
          shop: shopDomain,
          client_id: shopifyClientId.trim(),
          client_secret: shopifyClientSecret.trim()
        }
      });

      if (response.data.success && response.data.oauthUrl) {
        // Redirect to Shopify OAuth page
        window.location.href = response.data.oauthUrl;
      } else {
        throw new Error('Failed to get OAuth URL');
      }
    } catch (err) {
      console.error('OAuth initiation error:', err.response?.data || err.message);
      let errorMessage = 'Failed to initiate OAuth. Please try again.';
      
      if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      setIsConnecting(false);
    }
  };

  const handleConnect = async (e) => {
    e.preventDefault();
    setIsConnecting(true);
    setError(null);

    try {
      // Clean up the store URL
      let cleanUrl = storeUrl.trim();
      
      if (platform === 'shopify') {
        // For Shopify, ensure we're using HTTPS
        if (!cleanUrl.startsWith('http')) {
          cleanUrl = `https://${cleanUrl}`;
        }
        cleanUrl = cleanUrl.replace('http://', 'https://');
      } else {
        // For WooCommerce, handle localhost with HTTP
        if (cleanUrl.includes('localhost')) {
          cleanUrl = cleanUrl.replace('https://', 'http://');
        } else if (!cleanUrl.startsWith('http')) {
          cleanUrl = `http://${cleanUrl}`;
        }
      }
      
      cleanUrl = cleanUrl.replace(/\/$/, ''); // Remove trailing slash

      console.log('Connecting to store:', cleanUrl);
      
      // Validate required fields
      if (!cleanUrl) {
        throw new Error('Store URL is required');
      }

      if (platform === 'woocommerce' && (!consumerKey || !consumerSecret)) {
        throw new Error('Consumer key and secret are required for WooCommerce');
      }

      if (platform === 'shopify' && !accessToken) {
        throw new Error('Access token is required for Shopify');
      }

      const response = await api.post('/api/stores/connect-store', {
        platform,
        store_url: cleanUrl,
        ...(platform === 'woocommerce' ? {
          consumer_key: consumerKey.trim(),
          consumer_secret: consumerSecret.trim()
        } : {
          access_token: accessToken.trim()
        })
      });

      console.log('Store connection response:', response.data);

      if (response.data.status === 'success' || response.data.success) {
        console.log('Successfully connected to store');
        // Save store connection details
        const storeConnection = {
          platform,
          store_url: cleanUrl,
          ...(platform === 'woocommerce' ? {
            consumer_key: consumerKey,
            consumer_secret: consumerSecret
          } : {
            access_token: accessToken
          })
        };
        localStorage.setItem('store_connection', JSON.stringify(storeConnection));
        localStorage.setItem('platform', platform);
        localStorage.setItem('storeUrl', cleanUrl);
        localStorage.setItem('isConnected', 'true');
        
        // Call onConnected if provided, otherwise redirect directly
        if (onConnected && typeof onConnected === 'function') {
        onConnected(cleanUrl);
        } else {
          // Redirect to dashboard after successful connection (HashRouter requires #)
          window.location.href = '/#/dashboard';
        }
      } else {
        throw new Error(response.data.message || 'Failed to connect to store');
      }
    } catch (err) {
      console.error('Connection error:', err.response?.data || err.message);
      let errorMessage = 'Failed to connect to store. Please check your credentials and try again.';
      
      if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      // Add helpful message for SSL errors
      if (err.response?.data?.details?.message?.includes('certificate')) {
        errorMessage += ' For local development, try using http:// instead of https://.';
      }
      
      setError(errorMessage);
    } finally {
      setIsConnecting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          Connect Your Store
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Enter your store details to get started
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <form className="space-y-6" onSubmit={handleConnect}>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Platform
              </label>
              <select
                value={platform}
                onChange={(e) => setPlatform(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="woocommerce">WooCommerce</option>
                <option value="shopify">Shopify</option>
              </select>
            </div>

            <div>
              <label htmlFor="storeUrl" className="block text-sm font-medium text-gray-700">
                Store URL
              </label>
              <div className="mt-1">
                <input
                  id="storeUrl"
                  name="storeUrl"
                  type="text"
                  required
                  placeholder={platform === 'woocommerce' ? "https://your-store.com or http://localhost/wordpress" : "your-store.myshopify.com"}
                  value={storeUrl}
                  onChange={(e) => setStoreUrl(e.target.value)}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <p className="mt-1 text-xs text-gray-500">
                {platform === 'woocommerce' 
                  ? 'Enter your WooCommerce store URL (e.g., https://your-store.com or http://localhost/wordpress)'
                  : 'Enter your Shopify store URL (e.g., your-store.myshopify.com)'}
              </p>
            </div>

            {platform === 'woocommerce' ? (
              <>
                <div>
                  <label htmlFor="consumerKey" className="block text-sm font-medium text-gray-700">
                    Consumer Key
                  </label>
                  <div className="mt-1">
                    <input
                      id="consumerKey"
                      name="consumerKey"
                      type="text"
                      required
                      placeholder="ck_..."
                      value={consumerKey}
                      onChange={(e) => setConsumerKey(e.target.value)}
                      className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="consumerSecret" className="block text-sm font-medium text-gray-700">
                    Consumer Secret
                  </label>
                  <div className="mt-1">
                    <input
                      id="consumerSecret"
                      name="consumerSecret"
                      type="password"
                      required
                      placeholder="cs_..."
                      value={consumerSecret}
                      onChange={(e) => setConsumerSecret(e.target.value)}
                      className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </>
            ) : (
              <>
                {!useManualShopify ? (
                  <>
                    {/* Prefilled Instructions Box with copy buttons */}
                    <div className="mb-6 p-5 bg-white border-2 border-blue-300 rounded-lg shadow-sm">
                      <div className="flex items-center mb-4">
                        <div className="bg-blue-100 p-2 rounded-lg mr-3">
                          <span className="text-2xl">📋</span>
                        </div>
                        <div>
                          <h3 className="text-lg font-bold text-gray-900">Shopify App Setup Instructions</h3>
                          <p className="text-xs text-gray-600">Copy these values, then finish distribution and connect.</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        {/* Required Scopes */}
                        <div className="bg-gray-50 p-3 rounded border border-gray-200">
                          <label className="block text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">
                            Required Scopes (Select All 5)
                          </label>
                          <div className="bg-white p-2 rounded border border-gray-300 flex items-center justify-between">
                            <code className="text-xs text-gray-800 flex-1 break-all">
                              read_checkouts, read_products, read_orders, read_customers, write_price_rules
                            </code>
                            <button
                              type="button"
                              onClick={() => copyToClipboard('read_checkouts, read_products, read_orders, read_customers, write_price_rules', 'scopes')}
                              className="ml-2 px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition whitespace-nowrap"
                            >
                              {copiedField === 'scopes' ? '✓ Copied!' : 'Copy'}
                            </button>
                          </div>
                        </div>

                        {/* App URL */}
                        <div className="bg-gray-50 p-3 rounded border border-gray-200">
                          <label className="block text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">
                            App URL
                          </label>
                          <div className="bg-white p-2 rounded border border-gray-300 flex items-center justify-between">
                            <code className="text-xs text-gray-800 flex-1 break-all">https://www.cartresq.com</code>
                            <button
                              type="button"
                              onClick={() => copyToClipboard('https://www.cartresq.com', 'appUrl')}
                              className="ml-2 px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition whitespace-nowrap"
                            >
                              {copiedField === 'appUrl' ? '✓ Copied!' : 'Copy'}
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Redirect URL */}
                      <div className="bg-gray-50 p-3 rounded border border-gray-200 mb-4">
                        <label className="block text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">
                          Redirect URL (Most Important!)
                        </label>
                        <div className="bg-white p-3 rounded border-2 border-blue-300 flex items-center justify-between">
                          <code className="text-sm text-gray-800 flex-1 break-all font-mono">https://api.cartresq.com/api/stores/shopify/callback</code>
                          <button
                            type="button"
                            onClick={() => copyToClipboard('https://api.cartresq.com/api/stores/shopify/callback', 'redirectUrl')}
                            className="ml-3 px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition font-medium whitespace-nowrap"
                          >
                            {copiedField === 'redirectUrl' ? '✓ Copied!' : 'Copy URL'}
                          </button>
                        </div>
                      </div>

                      {/* Steps including Custom distribution */}
                      <div className="bg-blue-50 p-4 rounded border border-blue-200 mb-4">
                        <p className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                          <span className="mr-2">⚡</span>
                          Step-by-Step Setup:
                        </p>
                        <ol className="text-xs text-gray-700 space-y-2 list-decimal list-inside">
                          <li>Go to <strong>Shopify Admin → Settings → Apps and sales channels → Develop apps</strong></li>
                          <li>Click <strong>"Create an app"</strong> and name it "CartResQ"</li>
                          <li>Click <strong>"Configure Admin API scopes"</strong> and select the 5 scopes listed above</li>
                          <li>Set <strong>App URL</strong> to the value above</li>
                          <li>Add <strong>Redirect URL</strong> to the value above</li>
                          <li>Click <strong>"Save"</strong></li>
                          <li className="font-semibold text-blue-900">Go to <strong>"Distribution"</strong> tab → Select <strong>"Custom distribution"</strong></li>
                          <li className="font-semibold text-blue-900">Enter your store URL in the custom distribution field and click <strong>"Save"</strong></li>
                          <li>Go to <strong>"API credentials"</strong> tab and copy <strong>Client ID</strong> & <strong>Client Secret</strong></li>
                          <li>Paste them below and click <strong>"Connect Shopify Store"</strong> to open your dashboard</li>
                        </ol>
                      </div>

                      {/* Direct link to create app with sanitized domain */}
                      {storeUrl && (
                        <div className="mt-4">
                          <a
                            href={`https://${sanitizeShopDomain(storeUrl)}.myshopify.com/admin/settings/apps/develop/new`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center justify-center w-full px-4 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm font-semibold rounded-md hover:from-blue-700 hover:to-indigo-700 transition shadow-md"
                          >
                            <span className="mr-2">🚀</span>
                            Open Shopify Admin to Create App
                            <svg className="ml-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                            </svg>
                          </a>
                        </div>
                      )}
                    </div>

                    {/* Client credentials */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Shopify App Client ID <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={shopifyClientId}
                        onChange={(e) => setShopifyClientId(e.target.value)}
                        placeholder="Paste your Shopify app Client ID here"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Shopify App Client Secret <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="password"
                        value={shopifyClientSecret}
                        onChange={(e) => setShopifyClientSecret(e.target.value)}
                        placeholder="Paste your Shopify app Client Secret here"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div className="mb-4">
                      <button
                        type="button"
                        onClick={handleShopifyOAuth}
                        disabled={isConnecting || !shopifyClientId || !shopifyClientSecret || !storeUrl}
                        className={`w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
                          isConnecting || !shopifyClientId || !shopifyClientSecret || !storeUrl
                            ? 'bg-gray-400 cursor-not-allowed'
                            : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
                        }`}
                      >
                        {isConnecting ? 'Connecting...' : 'Connect Shopify Store'}
                      </button>
                    </div>

                    {/* Manual toggle */}
                    <div className="mt-2 flex items-center">
                      <input
                        type="checkbox"
                        id="useManualToggle"
                        checked={useManualShopify}
                        onChange={(e) => setUseManualShopify(e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="useManualToggle" className="ml-2 text-xs text-gray-600">
                        Use manual access token instead
                      </label>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="mb-3 flex items-center justify-between">
                <label htmlFor="accessToken" className="block text-sm font-medium text-gray-700">
                  Access Token
                </label>
                      <button
                        type="button"
                        onClick={() => setUseManualShopify(false)}
                        className="text-xs text-blue-600 hover:text-blue-800"
                      >
                        Use OAuth instead
                      </button>
                    </div>
                <div className="mt-1">
                  <input
                    id="accessToken"
                    name="accessToken"
                    type="password"
                    required
                    placeholder="shpat_..."
                    value={accessToken}
                    onChange={(e) => setAccessToken(e.target.value)}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                  </>
                )}
              </>
            )}

            <div className="text-sm text-gray-500">
              {platform === 'woocommerce' ? (
                <>
                  <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
                    <p className="font-semibold text-blue-900 mb-2">💡 Easy Way: Use CartResQ Official Plugin</p>
                    <p className="mb-2">Install CartResQ's official WordPress plugin to easily generate API keys:</p>
                    <ol className="list-decimal pl-4 mb-2 space-y-1">
                      <li>Install the <a href="https://wordpress.org/plugins/cartresq/" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline font-semibold">CartResQ plugin</a> from WordPress.org</li>
                      <li>Activate the plugin in your WordPress admin</li>
                      <li>Go to CartResQ {'>'} API Keys in your WordPress dashboard</li>
                      <li>Click "Generate API Key" - the plugin will automatically create the keys</li>
                      <li>Copy the Consumer Key and Consumer Secret from the plugin</li>
                    </ol>
                    <p className="text-xs text-blue-700 mt-2">The plugin handles all the configuration automatically!</p>
                  </div>
                  <div className="mt-3">
                    <p className="font-semibold mb-2">Manual Method:</p>
                    <ol className="list-decimal pl-4 space-y-1">
                      <li>Go to your WordPress admin panel</li>
                      <li>Navigate to WooCommerce {'>'} Settings {'>'} Advanced {'>'} REST API</li>
                      <li>Click "Add Key"</li>
                      <li>Set Description (e.g., "Cart Recovery")</li>
                      <li>Set User to an admin account</li>
                      <li>Set Permissions to "Read/Write"</li>
                      <li>Click "Generate API Key"</li>
                      <li>Copy the Consumer Key and Consumer Secret</li>
                    </ol>
                  </div>
                </>
              ) : (
                <>
                  {!useManualShopify ? (
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
                      <p className="font-semibold text-blue-900 mb-2">✨ OAuth Connection (Recommended)</p>
                      <p className="text-sm text-blue-700 mb-2">
                        Click "Connect with Shopify" above to securely connect your store. You'll be redirected to Shopify to approve the connection.
                      </p>
                      <p className="text-xs text-blue-600">
                        No need to create apps or copy tokens manually - we handle everything for you!
                      </p>
                    </div>
                  ) : (
                    <>
                      <p className="font-semibold mb-2">Manual Connection - To get your Shopify access token:</p>
                  <ol className="list-decimal pl-4 mt-2 space-y-1">
                    <li>Go to your Shopify admin panel</li>
                    <li>Navigate to Settings {'>'} Apps and sales channels</li>
                    <li>Click "Develop apps" (or "Develop apps for your store")</li>
                    <li>Click "Create an app" or select an existing app</li>
                    <li>Give your app a name (e.g., "CartResQ Cart Recovery")</li>
                    <li>Click "Create app"</li>
                    <li>Navigate to "API credentials" tab</li>
                    <li>Under "Admin API access scopes", configure the following permissions:</li>
                    <ul className="list-disc pl-8 mt-1 space-y-1">
                      <li><strong>Checkouts:</strong> Read abandoned checkouts ✓ (REQUIRED)</li>
                          <li><strong>Products:</strong> Read products ✓ (REQUIRED - for product details)</li>
                          <li><strong>Orders:</strong> Read orders ✓ (Recommended - for analytics)</li>
                          <li><strong>Customers:</strong> Read customers ✓ (Recommended - for customer data)</li>
                          <li><strong>Price Rules:</strong> Write price rules ✓ (REQUIRED - for creating discount coupons)</li>
                    </ul>
                    <li>Click "Save" to save the scopes</li>
                    <li>Click "Install app" button (if prompted)</li>
                    <li>After installation, click "Reveal token once" under "Admin API access token"</li>
                    <li>Copy the access token (starts with "shpat_")</li>
                  </ol>
                  <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                    <p className="font-semibold text-yellow-900 mb-1">⚠️ Important Settings to Check:</p>
                    <ul className="list-disc pl-4 space-y-1 text-xs">
                      <li><strong>Abandoned Checkout Recovery:</strong> Must be enabled in your Shopify store settings</li>
                      <li><strong>Store URL Format:</strong> Use your store's myshopify.com domain (e.g., your-store.myshopify.com)</li>
                      <li><strong>API Version:</strong> Ensure your app uses a supported API version (2023-10 or later recommended)</li>
                      <li><strong>Webhook Access:</strong> No webhooks needed for basic cart recovery - CartResQ uses polling</li>
                    </ul>
                  </div>
                  <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-md">
                    <p className="font-semibold text-green-900 mb-1">✓ Variables to Verify:</p>
                    <ul className="list-disc pl-4 space-y-1 text-xs">
                      <li><strong>Store URL:</strong> Must be in format: your-store.myshopify.com (no https:// needed)</li>
                      <li><strong>Access Token:</strong> Must start with "shpat_" (Admin API access token)</li>
                      <li><strong>App Status:</strong> Must be "Installed" (not just "Created")</li>
                      <li><strong>Permissions:</strong> "Read abandoned checkouts" scope must be enabled</li>
                    </ul>
                  </div>
                    </>
                  )}
                </>
              )}
            </div>

            {error && (
              <div className="rounded-md bg-red-50 p-4">
                <div className="flex">
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">
                      Connection failed
                    </h3>
                    <div className="mt-2 text-sm text-red-700">
                      <p>{error}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {platform === 'woocommerce' || (platform === 'shopify' && useManualShopify) ? (
            <div>
              <button
                type="submit"
                disabled={isConnecting}
                className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
                  isConnecting
                    ? 'bg-blue-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
                }`}
              >
                {isConnecting ? 'Connecting...' : 'Connect Store'}
              </button>
            </div>
            ) : null}
          </form>
        </div>
      </div>
    </div>
  );
};

export default StoreSetup; 